import { useCaptureException } from '@zenbusiness/application-commons-logging';

/**
 * @typedef CouponObject
 * @property {number} amount - The discount amount
 * @property {string} code - The coupon code
 * @property {number} originalPrice - The original price before discount
 * @property {number} price - The final price after discount
 */

/**
 * @typedef ProductObject
 * @property {object} pricing - The product's pricing information
 * @property {Array<object>} [pricing.coupons=[]] - Available coupons for the product
 * @property {number} [pricing.price=0] - Base price of the product
 */

/**
 * Custom hook to calculate product pricing with coupon discounts
 * @param {object} params - The input parameters
 * @param {string} params.couponCode - The coupon code to apply
 * @param {ProductObject} params.product - The product object
 * @returns {CouponObject} The calculated pricing information
 * @throws {Error} Throws if product pricing information is missing
 */
function useProductCoupon({ couponCode, product }) {
  const captureException = useCaptureException();

  if (!product?.pricing) {
    throw new Error('Invalid product: missing pricing information');
  }

  const { coupons = [], price = 0 } = product.pricing;

  // If no coupon code is specified, return the original price
  if (!couponCode) {
    return {
      originalPrice: price,
      price,
      percentage: 0
    };
  }

  const coupon = coupons.find((c) => c.code === couponCode);

  // If couponCode is specified but not found, track error and return original price
  if (!coupon) {
    captureException(
      `Coupon code "${couponCode}" not found in price list for product "${product.service}"`,
      {
        level: 'error',
        contexts: {
          product: {
            sku: product.service,
            coupon: couponCode
          }
        }
      }
    );
    return {
      originalPrice: price,
      price,
      percentage: 0
    };
  }

  // TODO: Remove discountPrice in the future in favor of a canonical field from the service.
  const discount = coupon.amount || 0;
  const discountPrice = Math.max(price - discount, 0);
  const percentage = Math.round((discount * 100) / price);

  return {
    ...coupon,
    originalPrice: price,
    price: discountPrice,
    percentage
  };
}

export default useProductCoupon;
